<template lang="pug">
div.neutral200
  v-container.pa-8(style="max-width: 900px;")
    div.d-flex.justify-center.align-center(v-if="isFetching" style="height: 80dvh;")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
    template(v-else)
      v-row(no-gutters align="center")
        v-col.shrink
          yz-btn.ml-n3(icon-only icon="mdi-arrow-left" size="lg" color="transparent" @click="onGoBack")
        v-col.ml-3
          .yz-heading-5.text-neutral-700 {{ id ? 'Edit package' : 'Add package' }}
      v-form(ref="form")
        //- Package details
        yz-card.mt-4(outlined)
          .pa-6
            div.d-flex.align-center
              .yz-heading-8.text-neutral-700 Package details
              v-spacer
              yz-btn(v-if="id" label="View package history" size="sm" @click="$router.push({ name: 'HQPackageHistory', params: { id, origin: 'HQEditPackage' } })")
            v-row.mt-3
              v-col(cols="12" sm="6")
                .yz-heading-9.text-neutral-500 Package name
                yz-text-field.mt-2(v-model="form.product.name" placeholder="Package name" size="lg" :rules="[v => (v && v.trim().length > 0) || 'This field is required']")

        //- Default pricing
        yz-card.mt-4(outlined)
          .pa-6
            .yz-heading-8.text-neutral-700 Default pricing
            .yz-regular-body-sm.text-neutral-500.mt-1 Set the default price for this package. This pricing will be shared across all branches.
            v-row.mt-3
              v-col(cols="12" sm="4")
                .yz-heading-9.text-neutral-500 Price
                yz-text-field.mt-2(v-model="form.price" placeholder="0" size="lg" :prefix="$currency.symbol" type="number" :rules="[v => (v !== null && v !== '' && Number(v) >= 0) || 'This field is required']")
              v-col(cols="12" sm="4")
                .yz-heading-9.text-neutral-500 Bundle value
                yz-text-field.mt-2(:value="bundleValue" placeholder="0" size="lg" :prefix="$currency.symbol" type="number" readonly disabled background-color="neutral-300")
              v-col(cols="12" sm="4")
                .yz-heading-9.text-neutral-500 Discount
                yz-text-field.mt-2(:value="discount" placeholder="0" size="lg" suffix="%" type="number" readonly disabled background-color="neutral-300")

            //- Override pricing section
            yz-divider.my-5
            .d-flex.align-start.justify-space-between
              div
                .yz-heading-9.text-neutral-700 Add default pricing override
                .yz-regular-body-sm.text-neutral-500.mt-1 This pricing will override the default pricing and adjusted according to your selected branches and pricing setup.
              yz-btn(v-if="priceOverrides.length === 0" label="Add price override" size="sm" @click="showOverridePricingDialog = true")
            template(v-if="priceOverrides.length > 0")
              .d-flex.align-center.justify-end.mt-4.mb-2
                yz-btn(label="Add price override" size="sm" @click="showOverridePricingDialog = true")
              yz-simple-table
                template(v-slot:default)
                  thead
                    tr
                      th.yz-medium-body-xs.text-neutral-700 Branches
                      th.yz-medium-body-xs.text-neutral-700 Override price
                      th
                  tbody
                    template(v-for="(override, oIdx) in priceOverrides")
                      tr(v-for="branchId in override.branches" :key="'ob-' + oIdx + '-' + branchId")
                        td.py-3
                          .yz-heading-9.text-neutral-700 {{ getBranchName(branchId) }}
                        td.py-3
                          .yz-regular-body-sm.text-neutral-700 {{ formatOverridePrice(override.price) }}
                        td.py-3
                          yz-btn(v-if="branchId === override.branches[0]" label="Edit" size="sm" outlined @click="onEditOverride(oIdx)")

        //- Assign to branches
        yz-card.mt-4(outlined)
          .pa-6
            .yz-heading-8.text-neutral-700 Assign to branches
            .yz-regular-body-sm.text-neutral-500.mt-1 Select which branches should have this package.
            template(v-if="branches.length > 0")
              .mt-4
                div.d-flex.align-center.justify-space-between.py-3(v-for="branch in branches" :key="branch.id" style="border-bottom: 1px solid var(--yz-neutral-300);")
                  .yz-regular-body-sm.text-neutral-700 {{ branch.name }}
                  yz-switch(v-model="branch.assigned" size="md")
            template(v-else-if="!isFetchingBranches")
              .text-center.py-6
                .yz-regular-body-sm.text-neutral-500 No branches available.

        //- Attach treatments
        yz-card.mt-4(outlined)
          .pa-6
            .yz-heading-8.text-neutral-700 Attach treatments
            .yz-regular-body-sm.text-neutral-500.mt-1 Search and select inventory and services to include in this package.
            v-autocomplete.mt-4.round-xl(ref="autocmplt" v-model="selectedProduct" :items="searchResults" item-text="name" return-object :search-input.sync="searchInput" placeholder="Search and select here" :loading="isSearching" outlined dense clearable height="48" hide-details="auto" hide-no-data attach append-icon="" @change="onAddTreatment" @blur="onClearAutocomplete")
              template(#prepend-inner)
                v-icon.mt-1(size="22") $yzi-search-sm
              template(#item="{ item }")
                div.py-3.px-2
                  .text-sm.font-normal {{ item.name }}
                    span(v-if="item.uom") &nbsp;({{ item.uom }})
                  .d-flex.mt-1
                    .d-flex.align-center.mr-4
                      template(v-if="hasStock(item)")
                        v-icon.mr-1(size="11" color="success") $yzi-circle
                        .text-xs.font-normal.neutral500--text In stock
                      template(v-else)
                        v-icon.mr-1(size="11" color="danger") $yzi-circle
                        .text-xs.font-normal.neutral500--text Out of stock
                    .d-flex.align-center.mr-4
                      v-icon.mr-1(size="13") {{ getTypeIcon(item) }}
                      .text-xs.font-normal.neutral500--text.text-capitalize {{ item.type }}

            template(v-if="treatments.length > 0")
              v-simple-table.mt-4(style="overflow: visible;")
                template(v-slot:default)
                  thead
                    tr
                      th.yz-bold-body-xs.text-neutral-700 Name
                      th.yz-bold-body-xs.text-neutral-700 Type
                      th.yz-bold-body-xs.text-neutral-700 Price
                      th.yz-bold-body-xs.text-neutral-700(style="width: 120px;") Stock
                      th.yz-bold-body-xs.text-neutral-700(style="width: 220px;") Qty per set
                      th(style="width: 50px;")
                  tbody
                    tr(v-for="treatment in treatments" :key="treatment.id")
                      td.py-3
                        .yz-medium-body-sm.text-neutral-700 {{ treatment.name }}
                      td.py-3
                        .yz-regular-body-sm.text-neutral-700 {{ treatment.service ? 'Service' : 'Inventory' }}
                      td.py-3
                        .yz-regular-body-sm.success500--text {{ treatment.price | displayPrice($currency) }}
                      td.py-3
                        template(v-if="treatment.inventory_enabled")
                          .d-flex.align-center.justify-space-between
                            .yz-medium-body-sm.text-blue-500 {{ treatment.inventory_qty }} {{ getStockUnit(treatment) }}
                            yz-tooltip
                              template(#tooltip)
                                div On hand: {{ treatment.inventory_qty || 0 }}
                                div Available: {{ treatment.available_qty || 0 }}
                                div Reserved: {{ treatment.reserved_qty || 0 }}
                                div Committed: {{ treatment.committed_qty || 0 }}
                              yz-icon.ml-2.mb-1(size="14" color="neutral500") $yzi-info-circle
                        .yz-regular-body-sm.text-neutral-700(v-else) -
                      td.py-3
                        .d-flex.align-center.gap-2
                          yz-text-field(v-model="treatment.quantity_per_set" placeholder="0" size="lg" type="number" :rules="[v => (!!v && Number(v) > 0) || 'Required']" hide-details width="80px")
                          yz-dropdown(v-if="treatment.uom_conversion_enabled && treatment.uom_conversions && treatment.uom_conversions.length" v-model="treatment.selected_uom" :items="getUomOptions(treatment)" :rules="getUomRules(treatment)" size="lg" width="120px" :attach="true")
                          .yz-regular-body-sm.text-neutral-500(v-else-if="treatment.uom") {{ treatment.uom }}
                      td.py-3
                        yz-btn(icon-only icon="$yzi-trash-03" size="md" @click="onRemoveTreatment(treatment)")
            template(v-else)
              div.mt-4.pa-8.text-center(style="border: 2px dashed var(--yz-neutral-400); border-radius: 10px;")
                v-avatar(color="neutral300" size="54")
                  v-icon(color="neutral500" size="30") $yzi-colors
                .yz-heading-8.text-neutral-700.mt-6 No treatments selected
                .yz-regular-body-sm.text-neutral-500.mt-1 Selected treatments will be displayed here

  override-package-pricing-dialog(v-if="showOverridePricingDialog" :open.sync="showOverridePricingDialog" :branches="overrideBranchList" :currency="$currency" :bundle-value="bundleValue" :existing-override="editingOverride" @save="onSaveOverride" @closed="onCloseOverrideDialog")

  v-footer.white(app inset padless elevation="3")
    v-container.px-8.py-2
      div.d-flex.align-center.justify-end.gap-2
        yz-btn(label="Cancel" size="lg" outlined @click="onGoBack")
        yz-btn(:label="id ? 'Save' : 'Finish'" size="lg" :loading="isLoading" @click="onSaveData")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import OverridePackagePricingDialog from '@/components/hq/inventory/packages/OverridePackagePricingDialog.vue'

export default {
  components: { OverridePackagePricingDialog },
  beforeRouteEnter (to, from, next) {
    if (from.name) {
      to.meta.hasPrevPath = true
    }
    next()
  },
  props: ['id'],
  data () {
    return {
      isLoading: false,
      isFetching: false,
      isFetchingBranches: false,
      isSearching: false,
      form: {
        product: { name: '' },
        price: null,
      },
      branches: [],
      originalBranchCompanyIds: [],
      priceOverrides: [],
      editingOverrideIndex: null,
      editingOverride: null,
      showOverridePricingDialog: false,
      treatments: [],
      selectedProduct: null,
      searchInput: null,
      searchResults: [],
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    hqCompanyId () {
      return this.company?.branch_of?.id || this.company?.id
    },
    bundleValue () {
      if (!this.treatments.length) return null
      const value = this.treatments.reduce((sum, t) => sum + (Number(t.quantity_per_set || 0) * t.price), 0)
      return (value / this.$currency.multiplier).toFixed(this.$currency.exponent)
    },
    discount () {
      if (!this.bundleValue || !this.form.price || Number(this.bundleValue) === 0) return null
      const perc = ((Number(this.bundleValue) - Number(this.form.price)) / Number(this.bundleValue)) * 100
      if (perc <= 0) return null
      return Number.isInteger(perc) ? perc : parseFloat(perc.toFixed(2))
    },
    overrideBranchList () {
      const usedBranches = new Set(this.priceOverrides.flatMap(o => o.branches))
      if (this.editingOverride) {
        this.editingOverride.branches.forEach(b => usedBranches.delete(b))
      }
      return this.branches
        .filter(b => !usedBranches.has(b.id))
        .map(b => ({ companyId: b.id, name: b.name, enabled: b.assigned }))
    },
  },
  watch: {
    searchInput (val) {
      if (val) {
        this.fetchTreatmentProducts(val)
      }
    }
  },
  mounted () {
    if (this.id) {
      this.fetchPackage()
    } else {
      this.fetchBranches()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async fetchPackage () {
      this.isFetching = true
      try {
        const resp = await this.$axios.get(`/clinics/${this.company.slug}/hq/packages/${this.id}`)
        const data = resp.data
        this.form = {
          product: { name: data.product.name },
          price: data.product.price / this.$currency.multiplier,
        }
        this.treatments = (data.treatments || []).map(t => ({ ...t, quantity_per_set: t.quantity_per_set, selected_uom: (t.per_set && t.per_set.uom) || t.uom || null }))
        await this.fetchBranches()
        const assignedBranches = (data.branches || []).filter(b => !b.deleted)
        const assignedCompanyIds = new Set(assignedBranches.map(b => b.company.id))
        this.originalBranchCompanyIds = [...assignedCompanyIds]
        this.branches = this.branches.map(b => ({ ...b, assigned: assignedCompanyIds.has(b.id) }))
        // Parse price overrides from branches
        const multiplier = this.$currency.multiplier
        const exponent = this.$currency.exponent
        for (const branch of assignedBranches) {
          if (branch.sync_hq_price === false) {
            this.priceOverrides.push({
              branches: [branch.company.id],
              price: (branch.price / multiplier).toFixed(exponent),
            })
          }
        }
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.validate()
        })
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    async fetchBranches () {
      this.isFetchingBranches = true
      try {
        const resp = await this.$axios.get(`/companies/${this.hqCompanyId}/branches`, { params: { os: 'clinic', page_size: -1 } })
        if (resp.data && resp.data.results) {
          this.branches = resp.data.results.map(b => ({ id: b.id, name: b.name, slug: b.slug, assigned: false }))
        }
      } catch {
        // silently fail
      } finally {
        this.isFetchingBranches = false
      }
    },
    fetchTreatmentProducts (search) {
      this.isSearching = true
      const params = {
        relations: 'medication,service',
        status: 'published',
        ordering: '-inventory_qty,medication,name',
        search
      }
      this.$axios.get(`/clinics/${this.company.slug}/treatment_products`, { params })
        .then(resp => {
          this.searchResults = resp.data.results || []
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isSearching = false
        })
    },
    onAddTreatment (product) {
      if (!product) return
      if (this.treatments.find(t => t.id === product.id)) return
      this.treatments.push({ ...product, quantity_per_set: 1, selected_uom: product.uom || null })
      this.$refs.autocmplt.blur()
    },
    onRemoveTreatment (treatment) {
      this.treatments = this.treatments.filter(t => t.id !== treatment.id)
    },
    onClearAutocomplete () {
      this.selectedProduct = null
      this.searchInput = null
      setTimeout(() => {
        this.searchResults = []
      }, 300)
    },
    hasStock (item) {
      if (!item.inventory_enabled) return true
      return item.inventory_qty > 0
    },
    getStockUnit (treatment) {
      if (treatment.uom_conversion_enabled && treatment.uom_conversions && treatment.uom_conversions.length) {
        return treatment.uom_conversions[treatment.uom_conversions.length - 1].convert_to || ''
      }
      return treatment.uom || ''
    },
    getTypeIcon (item) {
      if (item.medication) return '$yzi-colors'
      if (item.type === 'documents') return '$yzi-file-06'
      return 'mdi-briefcase-outline'
    },
    getUomRules (treatment) {
      const validUnits = this.getUomOptions(treatment)
      return [
        v => !!v || 'Unit is required',
        v => validUnits.includes(v) || 'Select valid unit'
      ]
    },
    getUomOptions (treatment) {
      const units = []
      if (treatment.uom_conversions) {
        treatment.uom_conversions.forEach(c => {
          if (c.uom && !units.includes(c.uom)) units.push(c.uom)
          if (c.convert_to && !units.includes(c.convert_to)) units.push(c.convert_to)
        })
      }
      return units
    },
    buildPayload () {
      const multiplier = Number(this.$currency.multiplier)
      const data = {
        product: {
          name: this.form.product.name,
          price: Math.round(Number(this.form.price) * multiplier)
        },
        treatments: this.treatments.map(t => ({
          product: t.id,
          quantity_per_set: Number(t.quantity_per_set),
          ...(t.selected_uom ? { uom: t.selected_uom } : {})
        })),
      }
      // Branches with override pricing
      if (this.branches.length > 0) {
        const overrideMap = new Map()
        this.priceOverrides.forEach(o => {
          o.branches.forEach(bId => overrideMap.set(bId, o.price))
        })
        data.branches = this.branches
          .filter(b => b.assigned || this.originalBranchCompanyIds.includes(b.id))
          .map(b => {
            if (b.assigned) {
              const override = overrideMap.get(b.id)
              if (override != null && override !== '') {
                return { company: b.id, deleted: false, sync_hq_price: false, price: Math.round(Number(override) * multiplier) }
              }
              return { company: b.id, deleted: false, sync_hq_price: true }
            }
            return { company: b.id, deleted: true }
          })
      }
      return data
    },
    async onSaveData () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      if (!this.treatments.length) {
        this.showMessage(['error', 'Please attach at least one treatment'])
        return
      }
      this.isLoading = true
      const payload = this.buildPayload()
      try {
        if (this.id) {
          await this.$axios.patch(`/clinics/${this.company.slug}/hq/packages/${this.id}`, payload)
          this.showMessage(['success', this.$t('success_message.update_successful')])
        } else {
          await this.$axios.post(`/clinics/${this.company.slug}/hq/packages`, payload)
          this.showMessage(['success', 'Package created successfully'])
        }
        this.onGoBack()
      } catch {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    getBranchName (branchId) {
      const branch = this.branches.find(b => b.id === branchId)
      return branch ? branch.name : branchId
    },
    formatOverridePrice (price) {
      const amount = Math.round(Number(price) * this.$currency.multiplier)
      return this.$options.filters.displayPrice(amount, this.$currency)
    },
    onEditOverride (index) {
      this.editingOverrideIndex = index
      this.editingOverride = JSON.parse(JSON.stringify(this.priceOverrides[index]))
      this.showOverridePricingDialog = true
    },
    onSaveOverride (overrideData) {
      const savedBranches = new Set(overrideData.branches)
      for (let i = this.priceOverrides.length - 1; i >= 0; i--) {
        if (i === this.editingOverrideIndex) continue
        this.priceOverrides[i].branches = this.priceOverrides[i].branches.filter(b => !savedBranches.has(b))
        if (this.priceOverrides[i].branches.length === 0) {
          this.priceOverrides.splice(i, 1)
          if (this.editingOverrideIndex !== null && i < this.editingOverrideIndex) {
            this.editingOverrideIndex--
          }
        }
      }
      if (this.editingOverrideIndex !== null) {
        this.$set(this.priceOverrides, this.editingOverrideIndex, overrideData)
      } else {
        this.priceOverrides.push(overrideData)
      }
      this.onCloseOverrideDialog()
    },
    onCloseOverrideDialog () {
      this.showOverridePricingDialog = false
      this.editingOverrideIndex = null
      this.editingOverride = null
    },
    onGoBack () {
      if (this.$route?.meta?.hasPrevPath) {
        this.$router.back()
        return
      }
      this.$router.push('/admin/hq/inventory/packages')
    },
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  }
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
}
</style>
